import styled from 'styled-components';
import { Button, Grid, Paper } from '@mui/material';
import colors from '../../../constant/colors';
import fontWeights from '../../../constant/fontWeights';

export const PageContainer = styled.div`
  padding: 24px 32px 40px;
  width: 100%;
  font-family: Poppins;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 16px 16px 32px;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 12px;
    /* Stack the heading above its buttons instead of wrapping them awkwardly. */
    > * {
      flex: 1 1 100%;
      min-width: 0;
    }
  }
`;

export const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 18px 0 22px;
`;

export const PrimaryButton = styled(Button)`
  && {
    height: 38px;
    padding: 0 18px;
    font-size: 14px;
    font-family: Poppins;
    color: ${colors.white};
    background: ${colors.primary};
    font-weight: ${fontWeights.fontWeight500};
    border-radius: 8px;
    text-transform: none;
    white-space: nowrap;
    &.Mui-disabled {
      color: ${colors.white};
      opacity: 0.6;
    }
  }
`;

export const OutlineButton = styled(Button)`
  && {
    height: 38px;
    padding: 0 16px;
    font-size: 14px;
    font-family: Poppins;
    color: ${colors.black1};
    border: 1px solid ${colors.grey3};
    background: ${colors.white};
    font-weight: ${fontWeights.fontWeight500};
    border-radius: 8px;
    text-transform: none;
    white-space: nowrap;
  }
`;

export const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 20px;
`;

export const ProductCardBox = styled(Paper)`
  && {
    border-radius: 12px;
    box-shadow: 0px 4px 16px ${colors.shadowGradient};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0px 8px 24px ${colors.shadowGradient};
    }
  }
`;

export const ProductImageBox = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  background: ${colors.white4};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ProductBody = styled.div`
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

export const ProductTitle = styled.div`
  font-size: 15px;
  font-weight: ${fontWeights.fontWeight500};
  color: ${colors.black1};
  line-height: 20px;
  cursor: pointer;
`;

export const ProductMeta = styled.div`
  font-size: 12px;
  color: ${colors.grey1};
`;

export const PriceText = styled.div`
  font-size: 16px;
  font-weight: ${fontWeights.fontWeight700};
  color: ${colors.black1};
`;

export const ComparePrice = styled.span`
  font-size: 12px;
  color: ${colors.grey1};
  text-decoration: line-through;
  margin-left: 8px;
  font-weight: 400;
`;

export const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding-top: 10px;
`;

export const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

export const SectionCard = styled(Paper)`
  && {
    border-radius: 12px;
    box-shadow: 0px 4px 16px ${colors.shadowGradient};
    padding: 20px;
    margin-bottom: 20px;

    @media (max-width: 600px) {
      padding: 16px;
      margin-bottom: 16px;
    }
  }
`;

export const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: ${fontWeights.fontWeight700};
  color: ${colors.black1};
  margin-bottom: 12px;
`;

export const BodyText = styled.div`
  font-size: 14px;
  color: ${colors.black1};
  line-height: 20px;
`;

export const MutedText = styled.div`
  font-size: 13px;
  color: ${colors.grey1};
  line-height: 18px;
`;

export const CartLine = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.white1};
  flex-wrap: wrap;
  &:last-child {
    border-bottom: 0;
  }
`;

export const Thumb = styled.div`
  width: 64px;
  height: 64px;

  @media (max-width: 600px) {
    width: 48px;
    height: 48px;
  }

  border-radius: 8px;
  background: ${colors.white4};
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const LineInfo = styled.div`
  flex: 1;
  min-width: 160px;

  @media (max-width: 600px) {
    min-width: 0;
  }
`;

export const QtyControl = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${colors.grey3};
  border-radius: 8px;
  overflow: hidden;
`;

export const QtyValue = styled.span`
  min-width: 34px;
  text-align: center;
  font-size: 14px;
  color: ${colors.black1};
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 14px;
  color: ${colors.black1};
  padding: 6px 0;
  /* A tracking number has no spaces; without this it pushes the row wider
     than the screen instead of breaking. */
  word-break: break-word;
  &.total {
    font-weight: ${fontWeights.fontWeight700};
    font-size: 16px;
    border-top: 1px solid ${colors.white1};
    margin-top: 6px;
    padding-top: 12px;
  }
`;

export const DetailGrid = styled(Grid)`
  margin-top: 8px;
`;

export const GalleryMain = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  background: ${colors.white4};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const GalleryThumbs = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

export const GalleryThumb = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  &[data-active='true'] {
    border-color: ${colors.green};
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const VariantChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 12px 0;
`;

export const Description = styled.div`
  font-size: 14px;
  color: ${colors.grey1};
  line-height: 22px;
  margin-top: 12px;
  white-space: pre-line;
`;

export const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const TimelineItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  font-size: 13px;
  color: ${colors.black1};
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${colors.primary};
    margin-top: 4px;
    flex-shrink: 0;
  }
  .when {
    color: ${colors.grey1};
    font-size: 12px;
  }
`;

export const InfoBanner = styled.div`
  background: ${colors.lightBackground};
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.black1};
  margin-bottom: 16px;
`;

export const PaginationRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

export const DeliverySteps = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  margin: 10px 0 2px;
`;

export const DeliveryStep = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${colors.grey1};
  .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${colors.grey3};
  }
  .label {
    font-weight: ${fontWeights.fontWeight500};
    color: ${colors.grey1};
  }
  .when {
    color: ${colors.grey1};
    opacity: 0.8;
  }
  .bar {
    width: 28px;
    height: 2px;
    background: ${colors.grey3};
    margin: 0 2px;
  }

  /* Once the three steps wrap onto their own lines the connectors point at
     nothing, so they go and the row keeps its full width for the dates. */
  @media (max-width: 600px) {
    width: 100%;
    font-size: 12px;
    .bar {
      display: none;
    }
    .when {
      margin-left: auto;
    }
  }
  &[data-done='true'] {
    .dot { background: ${colors.green}; }
    .label { color: ${colors.black1}; }
    .bar { background: ${colors.green}; }
  }
`;
